/**
 * Partnership page → Page tab → "Translations (NL · FR)": the Dutch and
 * French versions of the booking page's own texts. "Translate with AI"
 * makes them from the saved English (partnerships/page_translate.py);
 * every field can then be adjusted by hand. Page texts are saved with the
 * page (Save changes); placement names save straight away.
 */
import { useState } from 'react';
import { Button } from '../ui/Button';
import { CommitInput } from '../ui/CommitInput';
import { Icon } from '../ui/Icon';
import RichTextEditor from '../ui/RichTextEditor';
import { Partnership, PageTranslation, usePartnershipPlacements, usePatchPartnershipPlacement } from '../../lib/api/partnerships';

type L = 'nl' | 'fr';
const LABEL: Record<L, string> = { nl: 'Nederlands', fr: 'Français' };
const lbl: React.CSSProperties = { display: 'block', fontSize: 12.5, fontWeight: 500, color: 'var(--ink-3)', margin: '12px 0 5px' };

export default function TranslationsPanel({
  draft, patch, campaignId, dirty, translating, onTranslate,
}: {
  draft: Partnership;
  patch: (next: Partial<Partnership>) => void;
  campaignId: string;
  dirty: boolean;
  translating: boolean;
  onTranslate: () => void;
}) {
  const [lang, setLang] = useState<L>('nl');
  const all = (draft.translations ?? {}) as Record<string, unknown>;
  const tr = (all[lang] ?? {}) as PageTranslation;
  const st = draft.translation_status;
  const { data: cards = [] } = usePartnershipPlacements(campaignId);
  const patchCard = usePatchPartnershipPlacement(campaignId);

  const set = (field: keyof PageTranslation, value: unknown) =>
    patch({ translations: { ...all, [lang]: { ...tr, [field]: value } } });
  const faq = tr.faq ?? [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <Button variant="primary" onClick={onTranslate} disabled={translating || dirty}>
          <Icon name="sparkle" size={12} /> {translating ? 'Translating…' : st?.translated_at ? 'Translate again with AI' : 'Translate with AI'}
        </Button>
        <span style={{ fontSize: 12, color: st?.outdated ? 'var(--warn)' : 'var(--ink-4)' }}>
          {dirty
            ? 'Save your changes first — the translation starts from the saved English.'
            : !st?.translated_at
              ? 'Not translated yet: brands who pick NL or FR see the English texts (buttons and steps are always translated).'
              : st.outdated
                ? 'The English texts changed since the last translation — translate again (it replaces the Dutch and French texts).'
                : `Translated on ${new Date(st.translated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}. Adjust anything below.`}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        {(['nl', 'fr'] as L[]).map((l) => (
          <button key={l} type="button" className={`filter-pill ${l === lang ? 'active' : ''}`} onClick={() => setLang(l)}>{LABEL[l]}</button>
        ))}
      </div>

      <label style={lbl}>Intro</label>
      <textarea className="input" rows={4} value={tr.intro ?? ''} onChange={(e) => set('intro', e.target.value)} placeholder={draft.intro} />
      <label style={lbl}>Page intro</label>
      <textarea className="input" rows={2} value={tr.page_intro ?? ''} onChange={(e) => set('page_intro', e.target.value)} placeholder={draft.page_intro} />
      {draft.days_note && (
        <>
          <label style={lbl}>Day picker note</label>
          <textarea className="input" rows={2} value={tr.days_note ?? ''} onChange={(e) => set('days_note', e.target.value)} placeholder={draft.days_note} />
        </>
      )}
      {(draft.stats_title || draft.stats.length > 0) && (
        <>
          <label style={lbl}>Numbers strip — heading</label>
          <input className="input" value={tr.stats_title ?? ''} onChange={(e) => set('stats_title', e.target.value)} placeholder={draft.stats_title} />
          {draft.stats.length > 0 && (
            <>
              <label style={lbl}>Numbers strip — figures (write numbers the local way: 131.000 / 131 000)</label>
              <div style={{ display: 'grid', gap: 6 }}>
                {draft.stats.map((s, i) => {
                  const x = tr.stats?.[i] ?? { value: '', label: '' };
                  const setStat = (field: 'value' | 'label' | 'note', v: string) => {
                    const next = draft.stats.map((_, k) => ({ value: '', label: '', ...(tr.stats?.[k] ?? {}) }));
                    next[i] = { ...next[i], [field]: v };
                    set('stats', next);
                  };
                  return (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 2fr', gap: 6 }}>
                      <input className="input" value={x.value} placeholder={s.value} onChange={(e) => setStat('value', e.target.value)} />
                      <input className="input" value={x.label} placeholder={s.label} onChange={(e) => setStat('label', e.target.value)} />
                      <input className="input" value={x.note ?? ''} placeholder={s.note ?? ''} onChange={(e) => setStat('note', e.target.value)} />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
      <label style={lbl}>Practical info — heading</label>
      <input className="input" value={tr.extra_section_title ?? ''} onChange={(e) => set('extra_section_title', e.target.value)} placeholder={draft.extra_section_title} />
      <label style={lbl}>Practical info — text</label>
      <RichTextEditor key={lang} value={tr.brand_responsibilities ?? ''} onChange={(html) => set('brand_responsibilities', html)} placeholder="" />
      <label style={lbl}>Sign-up terms</label>
      <textarea className="input" rows={3} value={tr.participation_disclaimer ?? ''} onChange={(e) => set('participation_disclaimer', e.target.value)} placeholder={draft.participation_disclaimer} />
      {faq.length > 0 && (
        <>
          <label style={lbl}>FAQ</label>
          <div style={{ display: 'grid', gap: 8 }}>
            {faq.map((q, i) => (
              <div key={i} style={{ display: 'grid', gap: 4 }}>
                <input className="input" value={q.question} onChange={(e) => set('faq', faq.map((x, k) => (k === i ? { ...x, question: e.target.value } : x)))} />
                <textarea className="input" rows={2} value={q.answer} onChange={(e) => set('faq', faq.map((x, k) => (k === i ? { ...x, answer: e.target.value } : x)))} />
              </div>
            ))}
          </div>
        </>
      )}

      {cards.length > 0 && (
        <>
          <label style={{ ...lbl, marginTop: 18 }}>Placements (saved straight away)</label>
          <div style={{ display: 'grid', gap: 10 }}>
            {cards.map((c) => {
              const ct = (c.translations?.[lang] ?? {}) as { name?: string; description?: string; requirements?: string };
              const save = (field: 'name' | 'description' | 'requirements', v: string) =>
                patchCard.mutate({ id: c.id, translations: { ...(c.translations ?? {}), [lang]: { ...ct, [field]: v } } });
              return (
                <div key={c.id} style={{ border: '1px solid var(--border-soft)', borderRadius: 'var(--r-md)', padding: 10, display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>{c.display_name}</div>
                  <CommitInput value={ct.name ?? ''} placeholder={c.display_name} onCommit={(v) => save('name', v)} />
                  <CommitInput multiline rows={2} value={ct.description ?? ''} placeholder={c.description} onCommit={(v) => save('description', v)} />
                  {(c.requirements || ct.requirements) && (
                    <CommitInput value={ct.requirements ?? ''} placeholder={c.requirements} onCommit={(v) => save('requirements', v)} />
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
